import ArrowIcon from 'components/elements/icons/ArrowIcon'
import InlineText from 'components/elements/texts/InlineText'
import Text from 'components/elements/texts/Text'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

/**
 * 対象がCalendarLineProps型かチェックする
 * @param target チェック対象
 */
export const isCalendarLineProps = (
  target: any
): target is CalendarLineProps => {
  return target.selectedDate !== undefined
}

/** カレンダーのマスごとの基準データ */
const baseCell = {
  date: dayjs(),
  isDisabled: false,
  isSelected: false,
}

/** カレンダーの基準データ */
const baseData = [
  [baseCell, baseCell, baseCell, baseCell, baseCell, baseCell, baseCell],
  [baseCell, baseCell, baseCell, baseCell, baseCell, baseCell, baseCell],
  [baseCell, baseCell, baseCell, baseCell, baseCell, baseCell, baseCell],
  [baseCell, baseCell, baseCell, baseCell, baseCell, baseCell, baseCell],
  [baseCell, baseCell, baseCell, baseCell, baseCell, baseCell, baseCell],
  [baseCell, baseCell, baseCell, baseCell, baseCell, baseCell, baseCell],
]

/** カレンダー行 */
const CalendarLine: React.FC<CalendarLineProps & CalendarInfoProps> = (
  props
) => {
  const { selectedDate, minDate, maxDate } = props

  const [currentMonth, setCurrentMonth] = useState(selectedDate)
  const [calendarData, setCalendarData] = useState(baseData)

  // カレンダーデータの更新
  useEffect(() => {
    const monthInitDate = currentMonth.startOf('month').startOf('week')
    const calendarData = baseData.map((baseRow, rowIndex) => {
      return baseRow.map((_, cellIndex) => {
        const currentDate = monthInitDate.add(rowIndex * 7 + cellIndex, 'day')
        const noMinLimit = minDate
          ? currentDate.isSameOrAfter(minDate, 'day')
          : true
        const noMaxLimit = maxDate
          ? currentDate.isSameOrBefore(maxDate, 'day')
          : true
        return {
          date: currentDate,
          isSelected: currentDate.isSame(selectedDate, 'day'),
          isDisabled: !noMinLimit || !noMaxLimit,
        }
      })
    })
    setCalendarData(calendarData)
  }, [selectedDate, currentMonth, maxDate, minDate])

  // 表示する月の変更
  const changeMonth = useCallback(
    (diffMonth: number): void => {
      setCurrentMonth(currentMonth.add(diffMonth, 'month'))
    },
    [currentMonth]
  )

  // 日付とカレンダーの更新タイミングがずれた場合に検知して修正
  useEffect(() => {
    if (isNaN(currentMonth.date())) {
      setCurrentMonth(selectedDate)
    }
  }, [selectedDate, currentMonth])

  return (
    <CalendarArea>
      <div>
        <CalendarMonthRow>
          <ArrowWrapper
            onClick={(): void => {
              changeMonth(-1)
            }}
          >
            <ArrowIcon color="var(--color-text)" left />
            <div />
          </ArrowWrapper>
          <CalendarMonth>
            <Text fontSize="24px" bold>
              {currentMonth.month() + 1}
              <InlineText fontSize="20px" bold>
                月
              </InlineText>
            </Text>
            <Text fontSize="14px" bold color="#A0A3AA">
              {currentMonth.year()}
            </Text>
          </CalendarMonth>
          <ArrowWrapper
            onClick={(): void => {
              changeMonth(1)
            }}
          >
            <div />
            <ArrowIcon color="var(--color-text)" right />
          </ArrowWrapper>
        </CalendarMonthRow>
        <WeekdayRow>
          <BaseCell>日</BaseCell>
          <BaseCell>月</BaseCell>
          <BaseCell>火</BaseCell>
          <BaseCell>水</BaseCell>
          <BaseCell>木</BaseCell>
          <BaseCell>金</BaseCell>
          <BaseCell>土</BaseCell>
        </WeekdayRow>
      </div>
      <CalendarBody>
        {calendarData.map((row, index) => (
          <CalendarRow key={index}>
            {row.map((cell, index) => (
              <CalendarCell
                key={index}
                isSelected={cell.isSelected}
                isDisabled={cell.isDisabled}
                onClick={
                  cell.isDisabled
                    ? undefined
                    : (): void => {
                        props.selectDate && props.selectDate(cell.date)
                      }
                }
              >
                {cell.date.date()}
              </CalendarCell>
            ))}
          </CalendarRow>
        ))}
      </CalendarBody>
    </CalendarArea>
  )
}

/** CalendarLinePropsのtype定義 */
export type CalendarLineProps = {
  /** 選択中の日付 */
  selectedDate: dayjs.Dayjs
  /** 日付選択時コールバック */
  selectDate?: (date: dayjs.Dayjs) => void
}

/** CalendarInfoPropsのtype定義 */
export type CalendarInfoProps = {
  /** 最小日付 */
  minDate?: dayjs.Dayjs
  /** 最大日付 */
  maxDate?: dayjs.Dayjs
}

/** CalendarLineコンポーネントのルートのスタイル定義 */
const CalendarArea = styled.div`
  margin: 8px 12px 12px;
`

/** 前/次の月を切り替えるボタンのスタイル定義 */
const ArrowWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 48px;
  height: 40px;
  line-height: 40px;
`

/** 選択中の月と年の表示部分のスタイル定義 */
const CalendarMonthRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

/** 選択中の月の表示のスタイル定義 */
const CalendarMonth = styled.div`
  text-align: center;
`
/** 曜日以外のカレンダー入力部分のスタイル定義 */
const CalendarBody = styled.div`
  width: 100%;
`

/** ベースとなるカレンダーの列のスタイル定義 */
const BaseRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`
/** ベースとなるカレンダーの1セル分のスタイル定義 */
const BaseCell = styled.div`
  flex: 1;
  text-align: center;
`
/** 曜日列のスタイル定義 */
const WeekdayRow = styled(BaseRow)`
  display: flex;
  justify-content: center;
  width: 100%;
  margin: 3px 0 6px;
  font-weight: bold;
  font-size: 14px;
`

/** カレンダーの列のスタイル定義 */
const CalendarRow = styled(BaseRow)`
  height: 50px;
  font-weight: bold;
  font-size: 18px;
`

/** カレンダーの1セル分のスタイル定義 */
const CalendarCell = styled(BaseCell)<{
  isSelected?: boolean
  isDisabled?: boolean
}>`
  height: 50px;
  color: #222222;
  line-height: 50px;
  ${(props): string =>
    props.isSelected ? 'background: var(--color-main);' : ''}
  ${(props): string => (props.isSelected ? 'color: #FFFFFF;' : '')}
  ${(props): string => (props.isDisabled ? 'background: #F0F0F0;' : '')}
  ${(props): string => (props.isDisabled ? 'color: #A0A3AA;' : '')};
`

export default CalendarLine
